import type { ComponentType, LazyExoticComponent } from 'react';
import { Suspense, useEffect, useRef } from 'react';

import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { getLogin } from '@web-platform/shared-universal-redux/src/state';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';
import { logError, logOperationalEvent, logWarn } from 'saddlebag-logger';
import { v4 as uuid } from 'uuid';

import { getLoginStrings, LOGIN_MODAL_REFERRERS } from './constants';

import type {
  BundledConsentVariant,
  LoginFormProps,
  Source,
} from '@skyscanner-internal/global-components/login-form';

const LoginFormLazy: LazyExoticComponent<ComponentType<LoginFormProps>> = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "GlobalComponentLoginForm" */
      '@skyscanner-internal/global-components/login-form'
    ),
);

const VALID_BUNDLED_CONSENT_VARIANTS: BundledConsentVariant[] = ['A', 'B', 'C'];

export type GlobalLoginFormProps = {
  actionId?: string;
  authClient: LoginFormProps['authClient'];
  consumer: LoginFormProps['consumer'];
  isOpen: boolean;
  onClose: LoginFormProps['onClose'];
  screenName: string;
  source: Source;
  subscriptionClient: LoginFormProps['subscriptionClient'];
};

const generateActionId = () => `web1${uuid().toUpperCase()}`;

const LoginForm = ({
  actionId,
  authClient,
  consumer,
  isOpen,
  onClose,
  screenName,
  source,
  subscriptionClient,
}: GlobalLoginFormProps) => {
  const { locale, market } = getCulture();
  const loyaltyDiscountsEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_LOYALTY_DISCOUNTS,
  );
  const homepageDropsEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.HOMEPAGE_DROPS_ENABLED);
  const enabledLoginFormBundledConsentConfigValue = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.EXPERIMENT.ENABLE_LOGIN_FORM_BUNDLED_CONSENT,
  );
  const enabledLoginFormBundledConsent = VALID_BUNDLED_CONSENT_VARIANTS.find(
    (variant) => enabledLoginFormBundledConsentConfigValue === variant,
  );

  const isProductionEnv = process.env.NODE_ENV === 'production';

  const generatedActionId = useRef(generateActionId());

  useEffect(() => {
    if (!isOpen) {
      generatedActionId.current = generateActionId();
    }
  }, [isOpen]);

  const { referrer } = useSelector(getLogin);

  const experimentContext = [];

  if (
    loyaltyDiscountsEnabled &&
    (referrer === LOGIN_MODAL_REFERRERS.PRICING_OPTION_LOYALTY_CTA ||
      referrer === LOGIN_MODAL_REFERRERS.LOYALTY_POC)
  ) {
    experimentContext.push('LOYALTY_DISCOUNTS_POC');
  }

  if (homepageDropsEnabled && referrer === LOGIN_MODAL_REFERRERS.DROPS_CTA) {
    experimentContext.push('HOMEPAGE_DROPS');
  }

  if (enabledLoginFormBundledConsent) {
    experimentContext.push(`LOGIN_FORM_BUNDLED_CONSENT: ${enabledLoginFormBundledConsent}`);
  }

  return (
    <LoginFormLazy
      actionId={actionId ?? generatedActionId.current}
      authClient={authClient}
      experimentContext={experimentContext.join(',')}
      isOpen={isOpen}
      isProductionEnv={isProductionEnv}
      locale={locale}
      logger={{
        logOperationalEvent,
        logError,
        logWarn,
      }}
      market={market}
      onClose={onClose}
      screenName={screenName}
      strings={getLoginStrings(referrer, enabledLoginFormBundledConsent)}
      subscriptionClient={subscriptionClient}
      consumer={consumer}
      source={source}
      unstable__enableLoginFormBundledConsent={enabledLoginFormBundledConsent}
    />
  );
};

export const GlobalLoginForm = (props: GlobalLoginFormProps) => (
  <ErrorBoundary componentName="GlobalLoginFormLazy" fallback={null}>
    <Suspense fallback={null}>
      <LoginForm {...props} />
    </Suspense>
  </ErrorBoundary>
);
